import { getApiClient } from './api-client';
import {
  KNOWLEDGE_SERVICE_URL as knowledgePath,
  QUALITY_METRICS_SERVICE_URL as qualityMetricsPath,
} from '../constants/APIconstants';
import { DASHBOARD_PARAMS } from '../constants/knowledge.constants';

/**
 * Dashboard widget reads on the react-query client. The same endpoints remain on
 * `knowledge.service.api` / `quality-metric.service.api` behind the legacy
 * `{ data, error }` envelope, for mobile and the Redux actions.
 *
 * Partial — only the fields the dashboard rows read.
 * @typedef {Object} KnowledgeItem
 * @property {string} id
 * @property {string} title
 * @property {string[]} [ownerIds]
 *
 * The analytics endpoints rank ids without returning the items; callers hydrate
 * against `itemsById`.
 * @typedef {Object} KnowledgeItemRef
 * @property {string} id
 *
 * @typedef {Object} QualityMetric
 * @property {string} metricId
 * @property {string} title
 * @property {number} totalRecords
 * @property {number} totalSuccess
 * @property {number} groupGoal
 * @property {boolean} [invert]
 * @property {Array<object>} [trends]
 */

class KnowledgeDashboardService {
  /**
   * @param {{ workspaceId?: string, signal?: AbortSignal }} [args]
   * @returns {Promise<KnowledgeItem[]>}
   */
  async getRecentlyCreatedItems({ workspaceId, signal } = {}) {
    return this._getPaginated({
      sort: DASHBOARD_PARAMS.recentlyCreatedSort,
      workspaceId,
      signal,
    });
  }

  /**
   * @param {{ workspaceId?: string, signal?: AbortSignal }} [args]
   * @returns {Promise<KnowledgeItem[]>}
   */
  async getRecentlyUpdatedItems({ workspaceId, signal } = {}) {
    return this._getPaginated({
      sort: DASHBOARD_PARAMS.recentlyUpdatedSort,
      workspaceId,
      signal,
    });
  }

  /**
   * @param {{ signal?: AbortSignal }} [opts]
   * @returns {Promise<KnowledgeItemRef[]>}
   */
  async getItemsOpenEventsForOrganization({ signal } = {}) {
    return this._getOpenEvents('organization', signal);
  }

  /**
   * @param {{ signal?: AbortSignal }} [opts]
   * @returns {Promise<KnowledgeItemRef[]>}
   */
  async getItemsOpenEventsForUser({ signal } = {}) {
    return this._getOpenEvents('user', signal);
  }

  /**
   * @param {{ signal?: AbortSignal }} [opts]
   * @returns {Promise<QualityMetric[]>}
   */
  async getMyQualityMetrics({ signal } = {}) {
    const { metrics } = await getApiClient().get(`${qualityMetricsPath}user`, { signal });
    return metrics ?? [];
  }

  /** @returns {Promise<KnowledgeItem[]>} */
  async _getPaginated({ sort, workspaceId, signal }) {
    const { items } = await getApiClient().get(`${knowledgePath}items/paginated`, {
      params: {
        pageSize: DASHBOARD_PARAMS.pageSize,
        page: DASHBOARD_PARAMS.page,
        sort,
        direction: DASHBOARD_PARAMS.direction,
        workspaceId,
      },
      signal,
    });
    return items ?? [];
  }

  /** @returns {Promise<KnowledgeItemRef[]>} */
  async _getOpenEvents(scope, signal) {
    const { items } = await getApiClient().get(`${knowledgePath}items/analytics/${scope}`, {
      params: { pageSize: DASHBOARD_PARAMS.pageSize, page: DASHBOARD_PARAMS.page },
      signal,
    });
    return items ?? [];
  }
}

export default new KnowledgeDashboardService();
